import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { requireAuth } from '../utils/auth';
import useEmblaCarousel from 'embla-carousel-react';
import useBookCatalog from '../utils/useBookCatalog';
import BookCard from '../components/BookCard';
import TagBar from '../components/TagBar';
import HomeBanner from '../components/HomeBanner';
import WhatIsDeenLit from '../components/WhatIsDeenLit';
import HomeFeatures from '../components/HomeFeatures';
import HomeShowcase from '../components/HomeShowcase';
import { TAGS } from '../data/homeBooks';
import { useCategories, categorySlug } from '../utils/categories';
import { useActiveProfile } from '../utils/profile';
import '../styles/gallery.css';
import '../styles/profile.css';

function BookSlider({ title, books, tag, hasMore = false, loadMore }) {
  const navigate = useNavigate();
  const target = `/category/${tag || ''}`;
  const handleSeeAll = (e) => {
    e.preventDefault();
    if (requireAuth(navigate, target)) navigate(target);
  };
  const paused = React.useRef(false);
  const visible = React.useRef(false);
  const sectionRef = React.useRef(null);
  const hasBooks = books.length > 0;
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { align: 'start', slidesToScroll: 1, loop: !hasMore, containScroll: 'trimSnaps', duration: 22 }
  );
  const [canPrev, setCanPrev] = useState(true);
  const [canNext, setCanNext] = useState(true);

  const onSelect = useCallback((api) => {
    setCanPrev(api.canScrollPrev());
    setCanNext(api.canScrollNext());
    const lastVisible = Math.max(api.selectedScrollSnap(), ...api.slidesInView());
    if (hasMore && lastVisible >= books.length - 5) loadMore?.();
  }, [hasMore, books.length, loadMore]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect(emblaApi);
    emblaApi.on('select', onSelect).on('reInit', onSelect);
    return () => { emblaApi.off('select', onSelect).off('reInit', onSelect); };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!sectionRef.current) return;
    const observer = new IntersectionObserver(([entry]) => { visible.current = entry.isIntersecting; });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [hasBooks]);
  useEffect(() => {
    if (!emblaApi) return;
    const timer = setInterval(() => {
      if (paused.current || !visible.current || document.hidden || document.documentElement.classList.contains('tour-open')) return;
      if (emblaApi.canScrollNext()) emblaApi.scrollNext();
      else if (!hasMore) emblaApi.scrollTo(0);
    }, 2000);
    return () => clearInterval(timer);
  }, [emblaApi, hasMore]);

  if (!books || books.length === 0) return null;

  return (
    <div className="slider-section" ref={sectionRef} onMouseEnter={() => { paused.current = true; }} onMouseLeave={() => { paused.current = false; }} onFocusCapture={() => { paused.current = true; }} onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget)) paused.current = false; }}>
      <div className="slider-topbar">
        <button
          type="button"
          className="slider-arrow slider-arrow-prev"
          onClick={() => emblaApi?.scrollPrev()}
          disabled={!canPrev}
          aria-label="Previous"
        >
          <span className="slider-arrow-inner">
            <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
              <path d="M14 6l-6 6 6 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
        <button
          type="button"
          className="slider-arrow slider-arrow-next"
          onClick={() => emblaApi?.scrollNext()}
          disabled={!canNext}
          aria-label="Next"
        >
          <span className="slider-arrow-inner">
            <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
              <path d="M10 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </button>
      </div>
      <div className="slider-stage">
        <div className="slider-clip">
          <div className="slider-viewport" ref={emblaRef}>
            <div className="slider-track">
              {books.map((b, i) => (
                <div className="slider-slide" key={b._id}>
                  <BookCard book={b} index={i} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="slider-seeall-row">
        <Link to={target} onClick={handleSeeAll} className="slider-seeall">
          See all{title && tag !== 'all' ? ` ${title}` : ''}
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      </div>
    </div>
  );
}

export default function Home() {
  const [activeTag, setActiveTag] = useState('all');
  const [query, setQuery] = useState('');
  const activeProfile = useActiveProfile();

  // Student accounts shouldn't see an "assigned books" rail on the home
  // page — assignments belong to the teacher's surface. Teachers (and
  // anyone else who happens to have assignedBookIds on their active
  // profile) continue to see the slider as a quick jump to their list.
  const accountType = (() => {
    try { return JSON.parse(localStorage.getItem('adeeb-user') || 'null')?.accountType || null; }
    catch { return null; }
  })();
  const isStudent = accountType === 'student';

  // Books the teacher assigned to this profile. The profile picker
  // stores assignedBookIds on the active-profile localStorage entry, so
  // we already have the ids without an extra fetch. Filter the loaded
  // catalogue down to the assigned ones.


  const customCategories = useCategories();

  // Tags shown in the bar = built-ins + any custom categories from /api/categories
  // (skip duplicates by slug — e.g. "Fiction" already exists as a built-in)
  const allTags = useMemo(() => {
    const builtinSlugs = new Set(TAGS.map((t) => t.id.toLowerCase()));
    const builtinLabels = new Set(TAGS.map((t) => t.label.toLowerCase()));
    const extras = customCategories
      .filter((c) => c?.name)
      .filter((c) => {
        const slug = categorySlug(c.name);
        const label = c.name.toLowerCase();
        return !builtinSlugs.has(slug) && !builtinLabels.has(label);
      })
      .map((c) => ({
        id: `cat-${categorySlug(c.name)}`,
        label: c.name,
        glow: 'gold',
        category: c.name,
      }));
    return [...TAGS, ...extras];
  }, [customCategories]);

  const activeLabel = allTags.find(t => t.id === activeTag)?.label || '';
  const activeCustomCat = allTags.find(t => t.id === activeTag)?.category;
  const filters = useMemo(() => ({ tag: activeTag, category: activeCustomCat, q: query }), [activeTag, activeCustomCat, query]);
  const { items: apiBooks, nextOffset, error: catalogError, loadMore, retry } = useBookCatalog(filters, 12);
  const [assignedBooks, setAssignedBooks] = useState([]);
  const assignedIds = JSON.stringify(isStudent ? [] : activeProfile?.assignedBookIds || []);
  useEffect(() => {
    let alive = true;
    setAssignedBooks([]);
    Promise.all(JSON.parse(assignedIds).map(id => axios.get(`/api/books/${id}/preview`).then(r => r.data).catch(() => null)))
      .then(items => { if (alive) setAssignedBooks(items.filter(Boolean)); });
    return () => { alive = false; };
  }, [assignedIds]);
  const visibleBooks = apiBooks;

  return (
    <div className="gallery-wrapper">
      <HomeBanner />
      <WhatIsDeenLit />

      {assignedBooks.length > 0 && (
        <div className="gallery-section">
          <BookSlider title="Your teacher assigned" books={assignedBooks} tag="all" />
        </div>
      )}

      <div className="gallery-section">
        <TagBar tags={allTags} active={activeTag} onChange={setActiveTag} query={query} onQueryChange={setQuery} />
        <p role="status">{catalogError}{catalogError && <button className="slider-seeall" onClick={retry}>Retry loading books</button>}</p>
        <BookSlider title={activeLabel} books={visibleBooks} tag={activeTag} hasMore={nextOffset !== null} loadMore={catalogError ? undefined : loadMore} />
      </div>

      <HomeFeatures />
      <HomeShowcase />
    </div>
  );
}
